import { Box, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Column } from 'react-data-grid';
import MultiLineEllipsis from '../../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { sortIssues } from '../../active-issues/actions/active-issues-actions';
import { sortIssuesResolved } from '../../resolved-issues/actions/resolved-issues-actions';
import IssueScoreHeaderCell from '../components/IssuesTable/IssueScoreHeaderCell';
import ApplicationCell from '../../../elements/TableCells/ApplicationCell';
import IssueCategoryCell from '../components/IssueTableRow/IssueCategoryCell';
import DateFormat from '../../../elements/Date/DateFormat';
import OwnerCell from '../../../elements/TableCells/OwnerCell';
import IssueSeverityCell from '../components/IssueTableRow/IssueSeverityCell';
import IssuesActions from '../components/IssueTableRow/IssuesActions/IssuesActions';
import IssueOptionsContainer from '../containers/IssueOptionsContainer';
import { Issue, IssueSortKey } from '../types/issues-types';
import { RepoTypes } from 'common-types';

export const useIssuesTableHeader = ({
  withMenu,
  resolvedIssues = false,
}: UseIssuesTableHeaderOptions): Column<Issue>[] => {
  const { classes, cx } = useStyles();
  const columns: Column<Issue>[] = [
    {
      key: 'index',
      name: '#',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      cellClass: cx(classes.textCenter, classes.textSecondary),
      headerCellClass: classes.textCenter,
      sortable: false,
      headerRenderer: () => {
        return <>#</>;
      },
      formatter: ({ row }) => {
        return <>{row.index + 1}</>;
      },
    },
    {
      key: IssueSortKey.Severity,
      name: 'Severity',
      width: 90,
      minWidth: 90,
      maxWidth: 90,
      sortable: true,
      headerRenderer: props => (
        <IssueScoreHeaderCell
          {...props}
          onSort={resolvedIssues ? sortIssuesResolved : sortIssues}
        />
      ),
      formatter: ({ row }) => {
        return <IssueSeverityCell severity={row.severity} />;
      },
    },
    {
      key: IssueSortKey.Category,
      name: 'Category',
      width: 120,
      minWidth: 120,
      maxWidth: 120,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return <IssueCategoryCell issue={row} />;
      },
    },
    {
      key: IssueSortKey.IssueName,
      name: 'Name',
      formatter: ({ row }) => {
        return (
          <MultiLineEllipsis lines={2} variant='body2' color='text.primary'>
            {row.mainTitle}
          </MultiLineEllipsis>
        );
      },
    },
    {
      key: IssueSortKey.RepoName,
      name: 'Application',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return (
          <ApplicationCell
            appName={row.app.name}
            appType={row.app.type as RepoTypes}
          />
        );
      },
    },
    {
      key: IssueSortKey.Owner,
      name: 'Issue Owner',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      cellClass: classes.textSecondary,
      formatter: ({ row }) => {
        return <OwnerCell owners={row.owner} />;
      },
    },
  ];

  if (resolvedIssues) {
    columns.push(
      ...([
        {
          key: IssueSortKey.OpenDate,
          name: 'Discovered',
          width: 150,
          minWidth: 150,
          maxWidth: 150,
          formatter: ({ row: issue }) => (
            <DateFormat date={issue.createdDate} />
          ),
        },
        {
          key: IssueSortKey.ResolvedDate,
          name: 'Resolved',
          width: 150,
          minWidth: 150,
          maxWidth: 150,
          formatter: ({ row: issue }) => {
            return <DateFormat date={issue.resolvedIssueDate} />;
          },
        },
      ] as Column<Issue>[]),
    );
  } else {
    columns.push(
      ...([
        {
          key: IssueSortKey.OpenDate,
          name: 'First Seen Date',
          width: 150,
          minWidth: 150,
          maxWidth: 150,
          formatter: ({ row: issue }) => (
            <DateFormat date={issue.createdDate || issue.scanCreatedDate} />
          ),
        },
      ] as Column<Issue>[]),
    );
  }

  columns.push(
    ...([
      {
        key: IssueSortKey.Occurrences,
        name: 'Count',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        headerCellClass: classes.textCenter,
        cellClass: cx(classes.textSecondary, classes.textCenter),
        formatter: ({ row }) => <>{row.occurrences}</>,
      },
      {
        key: 'Actions',
        name: 'Actions',
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        sortable: false,
        headerCellClass: classes.textCenter,
        cellClass: cx(classes.textSecondary, classes.textCenter),
        formatter: ({ row }) => {
          return (
            <IssuesActions
              issueId={row.issueId}
              jiraTickets={row.jiraTickets}
              comment={row.comment}
              fixIssue={row.fixIssue}
              slackNotification={row.slackNotification}
            />
          );
        },
      },
    ] as Column<Issue>[]),
  );

  if (withMenu) {
    columns.push({
      key: 'MenuOptions',
      name: '',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      sortable: false,
      cellClass: classes.textCenter,
      formatter: ({ row }) => {
        return (
          <Box display='flex' justifyContent='center'>
            <IssueOptionsContainer issue={row} />
          </Box>
        );
      },
    });
  }

  return columns;
};

const useStyles = makeStyles()((theme: Theme) => ({
  textCenter: {
    justifyContent: 'center',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
  },
}));

interface UseIssuesTableHeaderOptions {
  withMenu?: boolean;
  resolvedIssues?: boolean;
}
